import { NetworkError } from '@api/api';
import ErrorTitle from '@components/errorTitle';
import { t, type FlatDict } from '@i18n';
import { notifications } from '@mantine/notifications';
import type { User } from '@models/user';
import type { OtherString } from '@models/util';
import monitoring from '@monitoring';
import { IconExclamationCircle } from '@tabler/icons-react';
import isPromise from '@util/isPromise';
import { calcDuration } from 'cross-state';
import { HTTPError } from 'ky';
import { get } from 'radashi';
import { createElement, useEffect, type ReactNode } from 'react';
import { ZodError } from 'zod';
import { fromZodError } from 'zod-validation-error';

export type KnownError = [match: Record<string, unknown>, message: keyof FlatDict | OtherString];

export interface HandleErrorOptions {
  title?: string;
  knownErrors?: KnownError[];
}

let seenErrors = new Array<unknown>();

export function errorToString(error: unknown, { includeStack }: { includeStack?: boolean } = {}) {
  if (error instanceof Error) {
    if (includeStack) {
      return error.stack ?? error.message;
    }

    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return JSON.stringify(error, null, 2);
}

export function handleError<TAction extends () => Promise<unknown>>(
  action: TAction,
  options?: HandleErrorOptions,
): Promise<boolean>;
export function handleError<TAction extends () => unknown>(action: TAction, options?: HandleErrorOptions): boolean;
export function handleError(error: unknown, options?: HandleErrorOptions): void;
export function handleError(errorOrAction: unknown, options?: HandleErrorOptions) {
  if (errorOrAction instanceof Function) {
    try {
      const result = errorOrAction();
      if (isPromise(result)) {
        return result
          .then(() => true)
          .catch((error) => {
            handleError(error, options);
            return false;
          });
      }
    } catch (error) {
      handleError(error, options);
      return false;
    }

    return true;
  }

  const error = errorOrAction instanceof Error ? errorOrAction : new Error(errorToString(errorOrAction));

  if (seenErrors.includes(error)) {
    return;
  }
  seenErrors = [...seenErrors, error].slice(-10);

  monitoring?.api.pushError(error);
  console.error(...[options?.title].filter(Boolean), error);

  const { title = options?.title, message } = matchError(error, options?.knownErrors);

  notifications.show({
    color: 'red',
    icon: createElement(IconExclamationCircle),
    title: createElement(ErrorTitle, {
      error,
      // oxlint-disable-next-line no-children-prop
      children: title,
    }),
    message:
      message &&
      createElement('div', {
        style: {
          margin: '10px 0',
          maxHeight: '5em',
          overflowY: 'auto',
          whiteSpace: 'pre-wrap',
        },
        // oxlint-disable-next-line no-children-prop
        children: message,
      }),
    withBorder: true,
    autoClose: calcDuration({ seconds: 10 }),
  });
}

export function getErrorDetails(error: unknown, user?: User) {
  return [
    `Time: ${new Date().toISOString()}`,
    `URL: ${window.location.href}`,
    `User agent: ${navigator.userAgent}`,
    `Version: ${config.VERSION}`,
    user && `User: ${user.userName}`,
    '---',
    errorToString(error, { includeStack: true }),
  ]
    .filter(Boolean)
    .join('\n');
}

export function matchError(error: unknown, knownErrors: KnownError[] = []): { title?: ReactNode; message?: ReactNode } {
  const matchingError = knownErrors.find(([match]) => {
    return Object.entries(match).every(([key, value]) => {
      return get(error, key) === value;
    });
  });

  if (matchingError) {
    return { message: t.unknown(matchingError[1], undefined, { fallback: matchingError[1] }) };
  }

  if (error instanceof HTTPError) {
    switch (error.response.status) {
      case 409:
        return { message: t('errors.conflict') };
    }

    switch (error.details?.status) {
      case 2:
        return { message: t('errors.tokenExpired') };
      case 3:
        return { message: t('errors.nameTaken') };
    }
  } else if (error instanceof NetworkError) {
    return { title: t('errors.network'), message: t('errors.networkMessage', { url: error.url }) };
  } else if (error instanceof ZodError) {
    return {
      title: t('errors.validation'),
      message: fromZodError(error, { issueSeparator: '\n\n' }).toString(),
    };
  }

  return {
    message: t.unknown(errorToString(error), undefined, { fallback: errorToString(error) }),
  };
}

export function useHandleError(errors?: unknown | unknown[], options?: HandleErrorOptions) {
  const error = (Array.isArray(errors) ? errors : [errors]).find(Boolean);

  useEffect(() => {
    if (error) {
      handleError(error, options);
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [error]);

  return function (errors: unknown | unknown[], options?: HandleErrorOptions) {
    const error = (Array.isArray(errors) ? errors : [errors]).find(Boolean);

    if (error) {
      handleError(error, options);
    }
  };
}
